/**
 * 媒体压缩统一分派入口
 *----------------------------------------------------
 * 上层（newUpload.vue）只需要传两个业务参数：
 *   1) format：压缩结果的目标格式（'auto' | 'gif' | 'video' | 'none'）
 *   2) ratio ：压缩比例（0~1，越大越清晰）
 *
 * 分派规则（只对视频生效，图片始终原样直传）：
 *   format='none'                              → 直传（返回原 file）
 *   format='auto' 且是视频                     → 走 video 管线（ffmpeg 同格式压缩）
 *   format='gif'  且是视频                     → 走 gif 管线（gif.js 转 GIF）
 *   format='video' 且是视频                    → 走 video 管线（ffmpeg 同格式压缩）
 *   非视频                                     → 直传（返回原 file）
 *
 * 失败降级：视频管线失败时静默降级为原 file，仅通过 onError 通知上层做 Toast。
 */
import { convertVideoToGif, isVideoFile, waitForFileReady } from './videoToGif';
import { compressVideo, canRunFfmpeg, isWechatWebView } from './videoToVideo';
import {
  compressVideoWithMediaRecorder,
  canRunMediaRecorderCompress,
} from './mediaRecorderCompressor';

/**
 * 允许的 format 取值
 */
export const COMPRESS_FORMATS = Object.freeze({
  AUTO: 'auto',
  GIF: 'gif',
  VIDEO: 'video',
  NONE: 'none',
});

/**
 * 归一化 format 值：兜底到 'none'（安全默认——不启用压缩）
 * @param {string} v
 * @returns {'auto'|'gif'|'video'|'none'}
 */
function normalizeFormat(v) {
  const s = typeof v === 'string' ? v.toLowerCase() : '';
  if (s === 'auto' || s === 'gif' || s === 'video' || s === 'none') return s;
  return 'none';
}

/**
 * 归一化 ratio：范围 (0, 1]，默认 0.6
 * @param {number} v
 * @returns {number}
 */
function normalizeRatio(v) {
  const n = Number(v);
  if (!isFinite(n) || n <= 0) return 0.6;
  if (n > 1) return 1;
  return n;
}

/**
 * 判断某个 format 是否会真正触发转换（帮助上层决定是否需要展示"转换中" Toast）
 * @param {File} file
 * @param {string} format
 */
export function willTransform(file, format) {
  const fmt = normalizeFormat(format);
  if (fmt === 'none') return false;
  if (!file) return false;
  // 仅视频文件会被处理
  return isVideoFile(file);
}

/**
 * 主入口：按 format/ratio 对文件做压缩
 * @param {File} file 原始文件
 * @param {Object} [options]
 * @param {'auto'|'gif'|'video'|'none'} [options.format='none']
 * @param {number} [options.ratio=0.6]                       压缩比例（越大越清晰）
 * @param {(p:number)=>void} [options.onProgress]            0~1
 * @param {(stage:string)=>void} [options.onStage]           阶段回调：'gif' | 'video' | 'load-ffmpeg'
 * @param {AbortSignal} [options.signal]                     取消信号（可选）：gif 管线会真正
 *                                                            中止抽帧与编码，不再后台空转
 * @returns {Promise<{ file: File|Blob, transformed: boolean, kind: 'gif'|'video'|'none' }>}
 */
export async function compressMedia(file, options = {}) {
  const format = normalizeFormat(options.format);
  const ratio = normalizeRatio(options.ratio);
  const { onProgress, onStage, signal } = options;

  const throwIfAborted = () => {
    if (signal && signal.aborted) {
      const err = new Error('压缩已取消');
      err.name = 'AbortError';
      throw err;
    }
  };

  // 不转换 or 非视频文件 → 原样返回
  if (format === 'none' || !file || !isVideoFile(file)) {
    return { file, transformed: false, kind: 'none' };
  }

  throwIfAborted();

  // 文件就绪等待（iOS 相册导出竞态兜底）：所有管线共用。
  // iOS 相册可能在系统导出/转码完成前就关闭相册并交付文件（size=0 / 尾部不可读），
  // 此时进任何转换管线都会永久卡死，降级上传原文件同样是不完整文件。
  // waitForFileReady：就绪零等待；未就绪每 2s 轮询，最长 30s，等不到抛 FILE_NOT_READY。
  await waitForFileReady(file, { signal, onStage });

  // 决定管线：auto === video
  const pipeline = format === 'gif' ? 'gif' : 'video';

  if (pipeline === 'gif') {
    try { onStage && onStage('gif'); } catch (_) { /* ignore */ }
    throwIfAborted();
    const gifFile = await convertVideoToGif(file, { ratio, onProgress, signal });
    return { file: gifFile, transformed: true, kind: 'gif' };
  }

  // video 管线：分环境选择实现
  // ─ 桌面/普通 Safari/Chrome：优先 ffmpeg.wasm（压缩率更好，且离线转码可加速）
  // ─ 企微/微信 WebView：ffmpeg.wasm 常挂起，改用 MediaRecorder + Canvas 纯浏览器 API
  try { onStage && onStage('video'); } catch (_) { /* ignore */ }
  throwIfAborted();

  const preferMediaRecorder = isWechatWebView() && canRunMediaRecorderCompress();
  if (preferMediaRecorder) {
    // 走 MediaRecorder 管线：不下载 wasm，秒级启动，处理时长 ≈ 视频时长/播放倍速
    try { onStage && onStage('mr-video'); } catch (_) { /* ignore */ }
    throwIfAborted();
    const outFile = await compressVideoWithMediaRecorder(file, {
      ratio,
      onProgress,
      onStage: (s) => {
        try { onStage && onStage(s); } catch (_) { /* ignore */ }
      },
    });
    return { file: outFile, transformed: true, kind: 'video' };
  }

  // ffmpeg.wasm 管线：环境预检
  if (!canRunFfmpeg()) {
    // 兜底再试 MediaRecorder（虽然 canRunFfmpeg 极少 false，防守型代码）
    if (canRunMediaRecorderCompress()) {
      throwIfAborted();
      const outFile = await compressVideoWithMediaRecorder(file, { ratio, onProgress, onStage });
      return { file: outFile, transformed: true, kind: 'video' };
    }
    throw new Error('当前环境不支持视频压缩（缺少 WebAssembly / Worker / MediaRecorder）');
  }
  // 把 videoToVideo 内部更细的 stage（load-ffmpeg-import / load-ffmpeg-core / transcoding）
  // 也透传给上层，让 UI 能区分"正在下载 30MB 内核"与"正在真正转码"两个阶段
  try {
    throwIfAborted();
    const outFile = await compressVideo(file, {
      ratio,
      onProgress,
      onStage: (s) => {
        try { onStage && onStage(s); } catch (_) { /* ignore */ }
      },
    });
    return { file: outFile, transformed: true, kind: 'video' };
  } catch (ffmpegErr) {
    // ffmpeg 失败（尤其 FFMPEG_UNSUPPORTED / FFMPEG_TIMEOUT）→ 二次降级到 MediaRecorder
    // 这样即使非企微环境（比如某个 iOS 老版本 Safari），ffmpeg 挂了也还有一层保底
    if (canRunMediaRecorderCompress()) {
      // eslint-disable-next-line no-console
      console.warn('[compressMedia] ffmpeg 失败，降级 MediaRecorder：', ffmpegErr);
      try { onStage && onStage('mr-video'); } catch (_) { /* ignore */ }
      throwIfAborted();
      const outFile = await compressVideoWithMediaRecorder(file, {
        ratio,
        onProgress,
        onStage: (s) => {
          try { onStage && onStage(s); } catch (_) { /* ignore */ }
        },
      });
      return { file: outFile, transformed: true, kind: 'video' };
    }
    throw ffmpegErr;
  }
}

export default compressMedia;
